<template>
  <div v-if="selections.length > 0" class="flex justify-between">
    <div class="flex flex-wrap">
      <div
        v-for="(selection, index) in selections"
        :key="index"
        class="flex mr-2 mb-2"
      >
        <div class="bg-blue-200 px-4 py-1 rounded-l-full flex items-center">
          <div>{{ selection.text }}</div>
        </div>
        <button
          class="bg-blue-500 px-2 pt-1 text-white rounded-r-full"
          @click="clearSelection(selection)"
        >
          <unicon
            name="times"
            fill="currentColor"
            width="20"
            height="20"
          ></unicon>
        </button>
      </div>
    </div>
    <div class="flex-wrap">
      <button
        class="bg-blue-500 text-white py-2 px-4 rounded-full ml-2"
        @click="clearSelections"
      >
        <nobr>Clear Selections</nobr>
      </button>
    </div>
  </div>
</template>
<script>
// import { mapState } from 'vuex'

export default {
  props: {
    /* eslint-disable camelcase */
    /**
     * Specific fields that can appear in this component
     * can be entered through includedFields. Use either
     * this or excludedFields. This array can either by a
     * an array of field names, or an array of objects with
     * fieldnames as field { field: fieldName }
     */
    includedFields: {
      type: Array,
      required: false,
      default: null
    },
    /**
     * If includedFields is not set, the excludedFields
     * prop can be used to remove specifc fields from being shown
     */
    excludedFields: {
      type: Array,
      required: false,
      default: () => []
    },
    /**
     * Individual values for fields will display up to and including this number.
     * If more than this number of values are selected, a pill for `+X More "Field Name"`
     * will display
     */
    maxDisplayedValuesForField: {
      type: Number,
      required: false,
      default: 4
    }
  },
  data() {
    return {
      selections: [],
      sessionObject: null
    }
  },
  computed: {
    totalSelections() {
      const _totalSelections = this.$store.getters['selections/totalSelections']
      if (typeof _totalSelections === 'undefined') {
        throw new TypeError(`The totalSelections getter is not defined in selections.js. This getter is a relatively
        new addition, so make sure the format of selections.js matches more recent mashups like the spree. The selections.js
        file should also have fields_with_selections state object.`)
      } else {
        return _totalSelections || 0
      }
    }
  },
  watch: {
    totalSelections() {
      this.update()
    }
  },
  async created() {
    try {
      // this is in case we do a page refresh
      if (this.sessionObject) {
        await this.removeQlikListeners()
      }
      // create a session object that will respond to user selections, will use the update methods to get changes
      this.sessionObject = await this.$qlik.generateSessionLists()
      this.sessionObject.on('changed', this.update)
      this.update()
    } catch (err) {
      console.log('error in QlikSelectedFitlerPills: initialize', err)
    }
  },
  methods: {
    async getSelectionsByField() {
      const selectionsByField = await this.$qlik.getCurrentSelectionsFromSessionLists(
        this.sessionObject
      )
      // remove any fields that are excluded
      if (this.includedFields) {
        Object.keys(selectionsByField).forEach(field => {
          const fieldName =
            typeof field === 'string' ? field : field?.field || ''
          if (
            !this.includedFields
              .map(_field =>
                typeof _field === 'string' ? _field : _field?.field || ''
              )
              .includes(fieldName)
          ) {
            delete selectionsByField[fieldName]
          }
        })
      } else if (this.excludedFields) {
        Object.keys(selectionsByField).forEach(field => {
          const fieldName =
            typeof field === 'string' ? field : field?.field || ''
          if (
            this.excludedFields
              .map(_field =>
                typeof _field === 'string' ? _field : _field?.field || ''
              )
              .includes(fieldName)
          ) {
            delete selectionsByField[fieldName]
          }
        })
      }
      return selectionsByField
    },
    /**
     * This method takes the selectionsByField object and turns it into a long list
     * of objects { key, value, text }
     * Additionally, if there are more than maxDisplayedValuesForField selections
     * display as +X More
     */
    flattenSelections(selectionsByField) {
      let selections = []
      Object.keys(selectionsByField).forEach(field => {
        // get all selections for this field, but then only include the first X
        const fieldSelections = selectionsByField[field]
          .map(({ text }) => ({
            key: field,
            value: text
          }))
          .filter((obj, index) => index < this.maxDisplayedValuesForField)
        // determine whether there are more selections in store than actually given here
        const stateSelections =
          this.$store?.state.selections?.current_selections[field] || []
        // console.log(stateSelections.length > fieldSelections.length)

        if (stateSelections.length > fieldSelections.length) {
          fieldSelections.push({
            key: field,
            value: `+${this.$store.state.selections.current_selections[field]
              .length - fieldSelections.length} More "${field}"`,
            isExtra: true
          })
        }
        selections = selections.concat(fieldSelections)
      })
      const values = selections.map(({ value }) => value)
      // find repeated values
      const repeats = values.filter(
        value => values.indexOf(value) !== values.lastIndexOf(value)
      )

      // create a "text" property that, if there are are any repeats of values, include key field
      selections = selections.map(({ key, value, isExtra }) => ({
        key,
        value: isExtra ? 'EXTRA' : value,
        text: repeats.includes(value) ? `${value} [${key}]` : value
      }))
      return selections
    },

    async clearSelections() {
      const selectionsByField = await this.getSelectionsByField()
      Object.keys(selectionsByField).forEach(field => {
        try {
          this.$qlik.clearField(field)
        } catch (error) {
          console.log(
            'error in QlikSelectedFilterPills: clearSelections',
            error,
            field
          )
        }
      })
    },

    async clearSelection({ key, value }) {
      try {
        if (value === 'EXTRA') {
          // if this is the + More box, then find all the values in the store that do not
          // match values stored here and remove them.
          if (
            this.$store?.state.selections?.current_selections[key] &&
            this.$store.state.selections.current_selections[key]?.length
          ) {
            this.$store.state.selections.current_selections[key].forEach(
              ({ text, number }) => {
                const fieldValues = this.selections
                  .filter(({ key: _key }) => _key === key)
                  .map(({ value }) => value)
                if (
                  !fieldValues.includes(text) &&
                  !fieldValues.includes(number)
                ) {
                  this.$qlik.toggleFieldValue(key, text)
                }
              }
            )
          }
        } else {
          await this.$qlik.toggleFieldValue(key, value)
        }
      } catch (error) {
        console.log(
          'error in QlikSelectedFilterPills: clearSelection',
          error,
          key
        )
      }
    },
    async removeQlikListeners() {
      if (this.sessionObject) {
        await this.sessionObject.removeListener('changed', this.handleChange)
        return await this.$qlik.destroySessionObject(this.sessionObject)
      } else {
        return false
      }
    },
    /**
     * Get current selections, update "fieldsWithSelections", and add any new fields to the altFieldValues array.
     */
    async update() {
      const selectionsByField = await this.getSelectionsByField()
      const flatSelections = this.flattenSelections(selectionsByField)
      // console.log(
      //   'selections',
      //   this.totalSelections,
      //   this.$store?.state.selections?.current_selections['School Name'],
      //   selectionsByField,
      //   flatSelections
      // )
      this.selections = flatSelections
    }
  }
}
</script>
